(() => {
  if (window.__helmaDoctorsReelsStableLoaded) return;

  window.__helmaDoctorsReelsStableLoaded = true;

  const instances = new WeakMap();


  class HelmaDoctorsReels {
    constructor(root) {
      this.root = root;

      this.carousel =
        root.querySelector('[data-doctors-carousel]');

      this.cards = Array.from(
        root.querySelectorAll('[data-doctor-card]')
      );

      this.prev =
        root.querySelector('[data-doctors-prev]');

      this.next =
        root.querySelector('[data-doctors-next]');

      this.autoplayActive =
        root.dataset.autoplayActive === 'true';

      this.activeCard = null;

      this.raf = null;
      this.settleTimer = null;

      this.sectionVisible = false;

      this.isDestroyed = false;


      if (!this.carousel || !this.cards.length) {
        return;
      }


      this.onScroll =
        this.onScroll.bind(this);

      this.onResize =
        this.onResize.bind(this);


      this.bindCards();
      this.bindCarousel();
      this.bindArrows();
      this.observeSection();


      /*
       * Wait for first layout before deciding
       * which card is actually centered.
       */

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.syncActiveFromCenter(true);
        });
      });


      /*
       * ResizeObserver keeps the active card
       * correct after viewport/layout changes.
       */

      if ('ResizeObserver' in window) {

        this.resizeObserver =
          new ResizeObserver(() => {
            this.requestSync();
          });

        this.resizeObserver.observe(
          this.carousel
        );

      }
    }


    /* =====================================================
       ACTIVE CARD
       ===================================================== */

    getClosestCardToCenter() {

      const carouselRect =
        this.carousel.getBoundingClientRect();

      const carouselCenter =
        carouselRect.left +
        carouselRect.width / 2;


      let closestCard = null;
      let smallestDistance = Infinity;


      this.cards.forEach((card) => {

        const rect =
          card.getBoundingClientRect();

        const cardCenter =
          rect.left +
          rect.width / 2;

        const distance =
          Math.abs(
            cardCenter -
            carouselCenter
          );


        if (distance < smallestDistance) {

          smallestDistance =
            distance;

          closestCard =
            card;

        }

      });


      return closestCard;
    }


    syncActiveFromCenter(initial = false) {

      if (this.isDestroyed) return;


      const closest =
        this.getClosestCardToCenter();


      if (!closest) return;


      if (
        closest !== this.activeCard ||
        initial
      ) {

        this.setActiveCard(
          closest,
          initial
        );

      }

    }


    setActiveCard(card, initial = false) {

      if (!card) return;


      const previous =
        this.activeCard;

      this.activeCard =
        card;


      const activeIndex =
        this.cards.indexOf(card);


      this.cards.forEach(
        (item, index) => {

          const isActive =
            item === card;


          item.classList.toggle(
            'is-active',
            isActive
          );


          item.classList.toggle(
            'is-before',
            index < activeIndex
          );


          item.classList.toggle(
            'is-after',
            index > activeIndex
          );

        }
      );


      /*
       * Immediately stop the card that
       * is no longer active.
       */

      if (
        previous &&
        previous !== card
      ) {

        this.pauseCard(previous);

      }


      /*
       * Don't autoplay while the user is
       * still swiping.
       *
       * Wait until scrolling settles.
       */

      clearTimeout(
        this.settleTimer
      );


      this.settleTimer =
        setTimeout(() => {

          /*
           * Re-check center after momentum/
           * scroll snap has fully finished.
           */

          const finalCenteredCard =
            this.getClosestCardToCenter();


          if (
            finalCenteredCard &&
            finalCenteredCard !==
              this.activeCard
          ) {

            this.setActiveCard(
              finalCenteredCard
            );

            return;

          }


          if (
            this.autoplayActive &&
            this.sectionVisible
          ) {

            this.playCard(
              this.activeCard,
              true
            );

          }

        }, initial ? 80 : 180);

    }


    /* =====================================================
       SCROLL
       ===================================================== */

    onScroll() {

      this.requestSync();


      /*
       * Extend settlement while native
       * momentum scrolling continues.
       */

      clearTimeout(
        this.settleTimer
      );


      this.settleTimer =
        setTimeout(() => {

          this.syncActiveFromCenter();


          if (
            this.autoplayActive &&
            this.sectionVisible &&
            this.activeCard
          ) {

            this.playCard(
              this.activeCard,
              true
            );

          }

        }, 180);

    }


    requestSync() {

      if (this.raf) return;


      this.raf =
        requestAnimationFrame(() => {

          this.raf = null;

          this.syncActiveFromCenter();

        });

    }


    onResize() {

      this.requestSync();

    }


    bindCarousel() {

      this.carousel.addEventListener(
        'scroll',
        this.onScroll,
        {
          passive: true
        }
      );


      window.addEventListener(
        'resize',
        this.onResize,
        {
          passive: true
        }
      );


      /*
       * Native scrollend where supported.
       * Gives an extra reliable final sync.
       */

      if ('onscrollend' in window) {

        this.carousel.addEventListener(
          'scrollend',
          () => {

            this.syncActiveFromCenter();


            if (
              this.autoplayActive &&
              this.sectionVisible &&
              this.activeCard
            ) {

              this.playCard(
                this.activeCard,
                true
              );

            }

          },
          {
            passive: true
          }
        );

      }

    }


    /* =====================================================
       VIDEO
       ===================================================== */

    pauseCard(card) {

      if (!card) return;


      const video =
        card.querySelector('video');


      if (!video) return;


      video.pause();


      card.classList.remove(
        'is-playing'
      );

    }


    pauseAllExcept(activeCard) {

      this.cards.forEach((card) => {

        if (card === activeCard) {
          return;
        }


        this.pauseCard(card);

      });

    }


    pauseAll() {

      this.cards.forEach((card) => {
        this.pauseCard(card);
      });

    }


    async playCard(
      card,
      autoplay = false
    ) {

      if (
        !card ||
        card !== this.activeCard
      ) {
        return;
      }


      const video =
        card.querySelector('video');


      if (!video) return;


      this.pauseAllExcept(card);


      /*
       * Browser autoplay requires muted
       * playback on most mobile browsers.
       */

      if (autoplay) {

        video.muted = true;


        card.classList.remove(
          'is-unmuted'
        );


        this.updateMuteButton(
          card,
          video
        );

      }


      try {

        await video.play();


        /*
         * The user might have swiped to
         * another card while play() awaited.
         */

        if (
          card !== this.activeCard ||
          !this.sectionVisible
        ) {

          video.pause();

          card.classList.remove(
            'is-playing'
          );

          return;
        }


        card.classList.add(
          'is-playing'
        );

      } catch (error) {

        card.classList.remove(
          'is-playing'
        );

      }

    }


    toggleVideo(card) {

      if (
        !card ||
        card !== this.activeCard
      ) {

        /*
         * Tapping a side card first brings
         * it cleanly to the center.
         */

        this.centerCard(card);

        return;
      }


      const video =
        card.querySelector('video');


      if (!video) return;


      if (video.paused) {

        this.playCard(
          card,
          false
        );

      } else {

        this.pauseCard(card);

      }

    }


    updateMuteButton(
      card,
      video
    ) {

      const button =
        card.querySelector(
          '[data-doctor-mute]'
        );


      if (!button) return;


      const isUnmuted =
        !video.muted;


      card.classList.toggle(
        'is-unmuted',
        isUnmuted
      );


      button.setAttribute(
        'aria-label',
        isUnmuted
          ? 'Mute video'
          : 'Unmute video'
      );

    }


    bindCards() {

      this.cards.forEach((card) => {

        const video =
          card.querySelector('video');

        const play =
          card.querySelector(
            '[data-doctor-play]'
          );

        const mute =
          card.querySelector(
            '[data-doctor-mute]'
          );


        if (!video) return;


        video.muted = true;
        video.playsInline = true;


        this.updateMuteButton(
          card,
          video
        );


        if (play) {

          play.addEventListener(
            'click',
            (event) => {

              event.preventDefault();
              event.stopPropagation();

              this.toggleVideo(card);

            }
          );

        }


        video.addEventListener(
          'click',
          () => {

            this.toggleVideo(card);

          }
        );


        video.addEventListener(
          'ended',
          () => {

            card.classList.remove(
              'is-playing'
            );

          }
        );


        if (mute) {

          mute.addEventListener(
            'click',
            (event) => {

              event.preventDefault();
              event.stopPropagation();


              /*
               * A side card cannot steal
               * audio before becoming active.
               */

              if (
                card !== this.activeCard
              ) {

                this.centerCard(card);

                return;

              }


              video.muted =
                !video.muted;


              this.updateMuteButton(
                card,
                video
              );


              if (video.paused) {

                this.playCard(
                  card,
                  false
                );

              }

            }
          );

        }

      });

    }


    /* =====================================================
       CENTER / ARROWS
       ===================================================== */

    centerCard(card) {

      if (!card) return;


      const carouselRect =
        this.carousel.getBoundingClientRect();

      const cardRect =
        card.getBoundingClientRect();


      const delta =
        (
          cardRect.left +
          cardRect.width / 2
        ) -
        (
          carouselRect.left +
          carouselRect.width / 2
        );


      this.carousel.scrollBy({
        left: delta,
        behavior: 'smooth'
      });


      /*
       * Don't manually force active here.
       * Physical center determines active.
       */

    }


    bindArrows() {

      this.prev?.addEventListener(
        'click',
        () => {

          this.moveBy(-1);

        }
      );


      this.next?.addEventListener(
        'click',
        () => {

          this.moveBy(1);

        }
      );

    }


    moveBy(direction) {

      const currentIndex =
        this.activeCard
          ? this.cards.indexOf(
              this.activeCard
            )
          : 0;


      const targetIndex =
        Math.max(
          0,
          Math.min(
            this.cards.length - 1,
            currentIndex + direction
          )
        );


      this.centerCard(
        this.cards[targetIndex]
      );

    }


    /* =====================================================
       SECTION VISIBILITY
       ===================================================== */

    observeSection() {

      this.sectionObserver =
        new IntersectionObserver(
          (entries) => {

            const entry =
              entries[0];


            this.sectionVisible =
              entry.isIntersecting &&
              entry.intersectionRatio > 0.12;


            if (
              !this.sectionVisible
            ) {

              clearTimeout(
                this.settleTimer
              );

              this.pauseAll();

              return;

            }


            this.requestSync();


            if (
              this.autoplayActive
            ) {

              clearTimeout(
                this.settleTimer
              );


              this.settleTimer =
                setTimeout(() => {

                  this.syncActiveFromCenter();


                  if (
                    this.activeCard
                  ) {

                    this.playCard(
                      this.activeCard,
                      true
                    );

                  }

                }, 180);

            }

          },
          {
            threshold: [
              0,
              0.12,
              0.3,
              0.6
            ]
          }
        );


      this.sectionObserver.observe(
        this.root
      );

    }


    /* =====================================================
       DESTROY
       ===================================================== */

    destroy() {

      this.isDestroyed = true;


      clearTimeout(
        this.settleTimer
      );


      if (this.raf) {

        cancelAnimationFrame(
          this.raf
        );

      }


      this.pauseAll();


      this.carousel.removeEventListener(
        'scroll',
        this.onScroll
      );


      window.removeEventListener(
        'resize',
        this.onResize
      );


      this.sectionObserver
        ?.disconnect();


      this.resizeObserver
        ?.disconnect();

    }
  }


  /* =======================================================
     INITIALIZATION
     ======================================================= */

  function init(root) {

    if (
      !root ||
      instances.has(root)
    ) {
      return;
    }


    const instance =
      new HelmaDoctorsReels(root);


    instances.set(
      root,
      instance
    );

  }


  function initAll(
    scope = document
  ) {

    scope
      .querySelectorAll(
        '[data-helma-doctors]'
      )
      .forEach(init);

  }


  function start() {
    initAll();
  }


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      start
    );

  } else {

    start();

  }


  /*
   * Shopify Theme Editor
   */

  document.addEventListener(
    'shopify:section:load',
    (event) => {

      initAll(event.target);

    }
  );


  document.addEventListener(
    'shopify:section:unload',
    (event) => {

      const root =
        event.target.querySelector(
          '[data-helma-doctors]'
        );


      if (!root) return;


      const instance =
        instances.get(root);


      instance?.destroy();

    }
  );

})();